import { Controller } from "@hotwired/stimulus"

// Tab-local drafts survive Turbo swaps, but never cross an identity boundary.
let draftState = null

export default class extends Controller {
  static targets = ["launcher", "panel", "badge", "messages", "scroll", "input", "send", "error", "empty", "older"]
  static values = { userId: Number, url: String, readUrl: String, sendError: String, loadError: String }

  connect() { this.resume() }

  disconnect() {
    this.suspend()
    queueMicrotask(() => {
      if (document.body.dataset.supportChatUser !== String(this.userIdValue)) draftState = null
    })
  }

  resume() {
    if (this.active || document.documentElement.hasAttribute("data-turbo-preview")) return
    if (document.body.dataset.supportChatUser !== String(this.userIdValue)) return
    this.active = true
    this.requests = new Set()
    this.cursor = 0
    this.firstId = null
    this.loading = false
    this.loaded = false
    this.sending = false
    this.sendTarget.disabled = false
    this.errorTarget.hidden = true
    this.olderTarget.disabled = false
    this.messagesTarget.replaceChildren()
    const state = draftState?.userId === this.userIdValue ? draftState : {}
    this.inputTarget.value = state.text || ""
    this.retry = state.retry
    this.opened = false
    this.viewportListener = () => this.viewport()
    window.visualViewport?.addEventListener("resize", this.viewportListener)
    window.visualViewport?.addEventListener("scroll", this.viewportListener)
    if (state.open || window.location.hash === "#support-chat") this.open()
    else this.poll()
  }

  remember() {
    draftState = { userId: this.userIdValue, text: this.inputTarget.value, open: this.opened, retry: this.retry }
  }

  suspend() {
    if (!this.active) return
    this.remember()
    this.active = false
    clearTimeout(this.timer)
    this.requests.forEach(request => request.abort())
    window.visualViewport?.removeEventListener("resize", this.viewportListener)
    window.visualViewport?.removeEventListener("scroll", this.viewportListener)
    this.unlock()
    this.panelTarget.hidden = true
    this.panelTarget.style.display = "none"
    this.badgeTarget.hidden = true
    this.badgeTarget.textContent = ""
    this.messagesTarget.replaceChildren()
    this.inputTarget.value = ""
    this.launcherTarget.setAttribute("aria-expanded", "false")
  }

  opener(event) {
    if (!event.target.closest("[data-support-chat-open]")) return
    event.preventDefault()
    this.open()
  }

  toggle() { this.opened ? this.close() : this.open() }

  open() {
    if (!this.active) return
    this.previousFocus = document.activeElement
    this.opened = true
    this.panelTarget.hidden = false
    this.panelTarget.style.display = "flex"
    this.launcherTarget.setAttribute("aria-expanded", "true")
    this.viewport()
    this.inputTarget.focus({ preventScroll: true })
    this.remember()
    this.poll()
  }

  close() {
    this.opened = false
    this.panelTarget.hidden = true
    this.panelTarget.style.display = "none"
    this.launcherTarget.setAttribute("aria-expanded", "false")
    this.unlock()
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true })
    this.remember()
    this.schedule()
  }

  viewport() {
    const mobile = window.matchMedia("(max-width: 639px)").matches
    this.panelTarget.setAttribute("aria-modal", String(mobile))
    if (!this.opened || !mobile) {
      this.unlock()
      this.panelTarget.style.height = ""
      this.panelTarget.style.top = ""
      return
    }
    if (!this.bodyStyle) {
      this.bodyStyle = { overflow: document.body.style.overflow, position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, scroll: window.scrollY }
      Object.assign(document.body.style, { overflow: "hidden", position: "fixed", top: `-${window.scrollY}px`, width: "100%" })
    }
    this.panelTarget.style.top = `${window.visualViewport?.offsetTop || 0}px`
    this.panelTarget.style.height = `${window.visualViewport?.height || window.innerHeight}px`
  }

  unlock() {
    if (!this.bodyStyle) return
    const { scroll, ...style } = this.bodyStyle
    Object.assign(document.body.style, style)
    window.scrollTo(0, scroll)
    this.bodyStyle = null
  }

  keyboard(event) {
    if (!this.opened) return
    if (event.key === "Escape") { event.preventDefault(); this.close() }
    if (event.key !== "Tab" || !window.matchMedia("(max-width: 639px)").matches) return
    const elements = [...this.panelTarget.querySelectorAll("button, textarea, input, a[href]")].filter(el => !el.disabled && !el.hidden && el.getClientRects().length)
    const first = elements[0], last = elements.at(-1)
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
  }

  visibility() {
    clearTimeout(this.timer)
    if (!document.hidden) this.poll()
  }

  schedule(delay = this.opened ? 5000 : 30000) {
    clearTimeout(this.timer)
    if (this.active && !document.hidden) this.timer = setTimeout(() => this.poll(), delay)
  }

  async request(url, options = {}) {
    const controller = new AbortController()
    const requests = this.requests
    requests.add(controller)
    try {
      const response = await fetch(url, { ...options, signal: controller.signal, credentials: "same-origin", cache: "no-store", headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", ...options.headers } })
      if (controller.signal.aborted || !this.active || requests !== this.requests) throw new DOMException("Aborted", "AbortError")
      if (!response.ok || response.redirected) throw new Error("Chat request failed")
      const data = response.status === 204 ? null : await response.json()
      if (controller.signal.aborted || !this.active || requests !== this.requests) throw new DOMException("Aborted", "AbortError")
      return data
    } finally { requests.delete(controller) }
  }

  async poll() {
    if (!this.active || document.hidden || this.loading) return
    this.loading = true
    const requests = this.requests
    let more = false
    const expanded = this.opened
    try {
      const query = expanded ? `after=${this.cursor}` : "summary=1"
      const data = await this.request(`${this.urlValue}?${query}`)
      if (!this.active || requests !== this.requests) return
      this.badgeTarget.hidden = data.unread === 0
      this.badgeTarget.textContent = String(data.unread)
      if (expanded && this.opened) {
        this.append(data)
        more = data.more
        if (this.cursor && !document.hidden) {
          await this.request(this.readUrlValue, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ through_id: this.cursor }) })
          if (this.active && this.opened) this.badgeTarget.hidden = true
        }
      }
    } catch (error) {
      if (error.name !== "AbortError" && this.opened) this.showError(this.loadErrorValue)
    } finally {
      if (requests === this.requests) { this.loading = false; this.schedule(more || (!expanded && this.opened) ? 0 : undefined) }
    }
  }

  append(data, prepend = false) {
    const scroller = this.scrollTarget
    const height = scroller.scrollHeight
    const nearBottom = height - scroller.scrollTop - scroller.clientHeight < 80
    const template = document.createElement("template")
    template.innerHTML = data.html
    template.content.querySelectorAll("[data-support-message-id]").forEach(message => {
      if (this.messagesTarget.querySelector(`[data-support-message-id="${message.dataset.supportMessageId}"]`)) message.remove()
    })
    if (prepend) this.messagesTarget.prepend(template.content)
    else this.messagesTarget.append(template.content)
    this.firstId = this.messagesTarget.firstElementChild?.dataset.supportMessageId
    this.cursor = Number(this.messagesTarget.lastElementChild?.dataset.supportMessageId || 0)
    this.emptyTarget.hidden = this.cursor > 0
    if (prepend || !this.loaded) this.olderTarget.hidden = !data.has_older
    if (prepend) scroller.scrollTop += scroller.scrollHeight - height
    else if (!this.loaded || nearBottom) scroller.scrollTop = scroller.scrollHeight
    this.loaded = true
  }

  async older() {
    if (!this.firstId) return
    const requests = this.requests
    this.olderTarget.disabled = true
    try { this.append(await this.request(`${this.urlValue}?before=${this.firstId}`), true) }
    catch (error) { if (error.name !== "AbortError") this.showError(this.loadErrorValue) }
    finally { if (this.active && requests === this.requests) this.olderTarget.disabled = false }
  }

  async send(event) {
    event.preventDefault()
    const body = this.inputTarget.value.trim()
    if (!body || this.sending) return
    this.sending = true
    this.sendTarget.disabled = true
    this.errorTarget.hidden = true
    if (!this.retry || this.retry.body !== body) this.retry = { body, idempotency_key: crypto.randomUUID() }
    this.remember()
    const request = this.retry
    const requests = this.requests
    try {
      await this.request(this.urlValue, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ support_message: request, after: this.cursor }) })
      if (this.inputTarget.value.trim() === body) this.inputTarget.value = ""
      this.retry = null
      this.remember()
      this.poll()
    } catch (error) {
      if (error.name !== "AbortError") this.showError(this.sendErrorValue)
    } finally {
      if (this.active && requests === this.requests) { this.sending = false; this.sendTarget.disabled = false }
    }
  }

  showError(text) { this.errorTarget.textContent = text; this.errorTarget.hidden = false }
}
